import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import config from "../config";
import BuzzShell from "./BuzzShell";

export default function BuzzZones() {
  const { t } = useTranslation();
  const [zones, setZones] = useState(null);
  const [name, setName] = useState("");
  const [editing, setEditing] = useState(null); // {id, name}
  const [error, setError] = useState(null);

  const load = useCallback(() => {
    axios.get(`${config.API_BASE_URL}/buzz/zones`).then(r => setZones(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    const n = name.trim();
    if (!n) return;
    setError(null);
    try {
      await axios.post(`${config.API_BASE_URL}/buzz/zones`, { name: n });
      setName("");
      load();
    } catch (e) {
      setError(e?.response?.status === 409 ? t("This zone already exists.") : t("Something went wrong — please try again."));
    }
  };

  const saveRename = async () => {
    const n = editing.name.trim();
    if (!n) return;
    try {
      await axios.patch(`${config.API_BASE_URL}/buzz/zones/${editing.id}`, { name: n });
      setEditing(null);
      load();
    } catch (e) {
      if (e?.response?.status === 409) setError(t("This zone already exists."));
    }
  };

  const move = async (idx, dir) => {
    const next = [...zones];
    const [item] = next.splice(idx, 1);
    next.splice(idx + dir, 0, item);
    await Promise.all(next.map((z, i) =>
      axios.patch(`${config.API_BASE_URL}/buzz/zones/${z.id}`, { sort: i })
    ));
    load();
  };

  const remove = async (z) => {
    const msg = z.tables > 0
      ? `${t("Delete zone")} «${z.name}»; ${z.tables} ${t("tables will be left without a zone.")}`
      : `${t("Delete zone")} «${z.name}»;`;
    if (!window.confirm(msg)) return;
    await axios.delete(`${config.API_BASE_URL}/buzz/zones/${z.id}`);
    load();
  };

  if (zones === null) return <BuzzShell><div className="bz-empty">…</div></BuzzShell>;

  return (
    <BuzzShell>
      <div className="bz-card">
        <h4 style={{ marginBottom: 4 }}>{t("Zones")}</h4>
        <p className="bz-hint">
          {t("The setup flow: 1) create zones, 2) assign tables to zones, 3) assign staff to zones — so each call notifies the right people.")}
        </p>
        <div className="bz-toolbar" style={{ marginTop: 10 }}>
          <input type="text" className="bz-input" placeholder={t("New zone (e.g. Garden)")} value={name} maxLength={45}
            style={{ flex: "1 1 220px" }}
            onChange={e => setName(e.target.value)}
            onKeyDown={e => e.key === "Enter" && add()} />
          <button className="bz-btn primary" onClick={add} disabled={!name.trim()}>➕ {t("Add zone")}</button>
        </div>
        {error && <div className="bz-banner err">{error}</div>}
      </div>

      {zones.length === 0 ? (
        <div className="bz-empty">
          {t("No zones yet. If your venue is a single area you can skip zones entirely — everyone gets every call.")}
        </div>
      ) : (
        <div className="bz-tablewrap">
        <table className="bz-table-list">
          <thead>
            <tr>
              <th style={{ width: 70 }}>{t("Order")}</th>
              <th>{t("Zone")}</th>
              <th>{t("Tables")}</th>
              <th>{t("Staff")}</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {zones.map((z, i) => (
              <tr key={z.id}>
                <td style={{ whiteSpace: "nowrap" }}>
                  <button className="bz-btn ghost" disabled={i === 0} onClick={() => move(i, -1)}>↑</button>
                  <button className="bz-btn ghost" disabled={i === zones.length - 1} onClick={() => move(i, +1)}>↓</button>
                </td>
                <td>
                  {editing?.id === z.id ? (
                    <span style={{ display: "inline-flex", gap: 6 }}>
                      <input type="text" className="bz-input" value={editing.name} autoFocus maxLength={45}
                        style={{ minWidth: 120 }}
                        onChange={e => setEditing({ ...editing, name: e.target.value })}
                        onKeyDown={e => { if (e.key === "Enter") saveRename(); if (e.key === "Escape") setEditing(null); }} />
                      <button className="bz-btn primary" onClick={saveRename}>✓</button>
                      <button className="bz-btn" onClick={() => setEditing(null)}>✕</button>
                    </span>
                  ) : (
                    <b style={{ cursor: "pointer" }} title={t("Rename")}
                       onClick={() => setEditing({ id: z.id, name: z.name })}>{z.name} ✏️</b>
                  )}
                </td>
                <td>{z.tables}</td>
                <td>{z.staff} 👤</td>
                <td style={{ textAlign: "right" }}>
                  <button className="bz-btn ghost" title={t("Delete")} onClick={() => remove(z)}>🗑</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      )}
    </BuzzShell>
  );
}
